import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { getMemoList, createMemo, modifyMemo, deleteMemos, Memo } from '../../api/memoApi';

// --- 스타일 (알림 관리자와 통일감 있는 디자인) ---
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid var(--border-color);
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 5px 10px;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background: ${props => props.active ? 'var(--primary-accent)' : 'var(--bg-input)'};
  color: ${props => props.active ? 'white' : 'var(--text-bright)'};
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { border-color: var(--primary-accent); }
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.5); z-index: 10001;
  display: flex; justify-content: center; align-items: center;
`;

const ModalBox = styled.div`
  background: var(--bg-card); width: 800px; max-width: 95vw; max-height: 85vh;
  padding: 24px; border-radius: 12px; border: 1px solid var(--border-color);
  color: var(--text-bright); display: flex; flex-direction: column; gap: 20px;
`;

const MemoGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 15px; overflow-y: auto; padding-right: 5px;
`;

const MemoCard = styled.div`
  background: var(--bg-input); padding: 15px; border-radius: 8px;
  border: 1px solid var(--border-color); position: relative;
  transition: transform 0.2s; cursor: pointer;
  &:hover { transform: translateY(-3px); border-color: var(--primary-accent); }
`;

const MemoText = styled.p`
  font-size: 0.9rem; margin: 0 0 10px 0; line-height: 1.4;
  white-space: pre-wrap; word-break: break-all;
`;

const MemoDate = styled.span`font-size: 0.75rem; color: var(--text-dark);`;

const InputArea = styled.textarea`
  width: 100%; height: 120px; padding: 12px; border-radius: 8px;
  background: var(--bg-input); color: var(--text-bright);
  border: 1px solid var(--border-color); resize: none; outline: none;
  &:focus { border-color: var(--primary-accent); }
`;

const DeleteBtn = styled.button`
  position: absolute; top: 8px; right: 8px; background: none; border: none;
  color: #ff4d4f; cursor: pointer; opacity: 0; 
  ${MemoCard}:hover & { opacity: 1; }
`;

// --- 컴포넌트 로직 ---
interface MemoManagerProps {
    isOpen: boolean;
    onClose: () => void;
}

const MemoManager = ({ isOpen, onClose }: MemoManagerProps) => {
    const [memos, setMemos] = useState<Memo[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const pageSize = 9; // Postman 기본값 준수

    const fetchMemos = useCallback(async (page: number) => {
        try {
            const res = await getMemoList(page, pageSize);
            setMemos(res.data.content);
            setTotalPages(res.data.totalPages);
            setTotalElements(res.data.totalElements);
        } catch (e) { console.error("메모 로드 실패", e); }
    }, [pageSize]); // pageSize는 상수이므로 fetchMemos는 거의 고정됩니다.

    useEffect(() => {
        if (isOpen) {
            fetchMemos(currentPage);
        }
    }, [isOpen, currentPage, fetchMemos]);

    const handleSave = async () => {
        if (!inputValue.trim()) return;
        try {
            if (editingId) {
                await modifyMemo(editingId, inputValue);
            } else {
                await createMemo(inputValue);
                setCurrentPage(0); // 새 메모 작성 시 첫 페이지로 이동
            }
            setInputValue('');
            setEditingId(null);
            fetchMemos(currentPage);
        } catch (e) { alert("저장 실패"); }
    };
    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!window.confirm("메모를 삭제할까요?")) return;
        try {
            await deleteMemos([id]);
            fetchMemos(currentPage);
        } catch (e) { alert("삭제 실패"); }
    };

    const startEdit = (memo: Memo) => {
        setEditingId(memo.id);
        setInputValue(memo.memo);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // 모달이 열려 있는 상태에서 ESC 키를 누르면 onClose 호출
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        // 이벤트 리스너 등록
        window.addEventListener('keydown', handleKeyDown);

        // 컴포넌트 언마운트 시 리스너 제거
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]); // isOpen이나 onClose가 변경될 때마다 리스너 업데이트

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalBox onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>📝 내 메모 ({totalElements}개)</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-bright)', cursor: 'pointer' }}>✕</button>
                </div>

                {/* 메모 작성 영역 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <InputArea
                        placeholder="새로운 메모를 입력하세요..."
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        {editingId && <button onClick={() => { setEditingId(null); setInputValue(''); }} style={{ padding: '8px 16px', borderRadius: '4px', backgroundColor: 'grey', color: 'white', border: 'none', cursor: 'pointer' }}>취소</button>}
                        <button
                            onClick={handleSave}
                            style={{ padding: '8px 16px', borderRadius: '4px', backgroundColor: 'var(--primary-accent)', color: 'white', border: 'none', cursor: 'pointer' }}
                        >
                            {editingId ? '수정 완료' : '메모 추가'}
                        </button>
                    </div>
                </div>

                {/* 메모 목록 영역 */}
                <MemoGrid>
                    {memos.map(memo => (
                        <MemoCard key={memo.id} onClick={() => startEdit(memo)}>
                            <DeleteBtn onClick={(e) => handleDelete(e, memo.id)}>✕</DeleteBtn>
                            <MemoText>{memo.memo}</MemoText>
                            <MemoDate>{new Date(memo.modifyDateTime).toLocaleString()}</MemoDate>
                        </MemoCard>
                    ))}
                </MemoGrid>

                {totalPages > 0 && (
                    <Pagination>
                        <PageButton
                            disabled={currentPage === 0}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                        >
                            이전
                        </PageButton>

                        {[...Array(totalPages)].map((_, i) => (
                            <PageButton
                                key={i}
                                active={currentPage === i}
                                onClick={() => setCurrentPage(i)}
                            >
                                {i + 1}
                            </PageButton>
                        ))}

                        <PageButton
                            disabled={currentPage === totalPages - 1}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                        >
                            다음
                        </PageButton>
                    </Pagination>
                )}
            </ModalBox>
        </ModalOverlay>
    );
};

export default MemoManager;